import React, { useContext, useEffect, useState } from 'react';
import { toast } from '../../lib/toast';
import { AppContext } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import PickerSheet from '../ui/picker-sheet';
import { getAllLocations, getAllFloors } from '../../lib/deviceUiModel';
import { PREDEFINED_DEVICE_TYPES } from '../../lib/deviceConstants';
import { cn } from '../../lib/utils';

const deepEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};

const DeviceEditor = ({ deviceId, devices, onClose }) => {
  const { updateDeviceMetadata, refreshDevices } = useContext(AppContext);

  const currentDevice = devices?.find(d => d.id === deviceId);

  const [original, setOriginal] = useState({
    name: '',
    location: '',
    floor: '',
    type: '',
  });

  const [draft, setDraft] = useState({
    name: '',
    location: '',
    floor: '',
    type: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const hasChanges = !deepEqual(draft, original);

  const isValidName = (name) => name.trim().length > 0;

  const isValid = isValidName(draft.name);

  const isDuplicateLocation = (location) => {
    if (!location) return false;
    return devices.some(d =>
      d.id !== deviceId &&
      d.location?.toLowerCase() === location.toLowerCase()
    );
  };

  const isDuplicateFloor = (floor) => {
    if (!floor) return false;
    return devices.some(d =>
      d.id !== deviceId &&
      d.floor?.toLowerCase() === floor.toLowerCase()
    );
  };

  const handleChange = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    onClose && onClose();
  };

  const handleSave = async () => {
    if (!hasChanges || !isValid) return;

    try {
      setIsSaving(true);

      const metadata = {
        name: draft.name,
        location: draft.location,
        floor: draft.floor,
        type: draft.type,
      };

      await updateDeviceMetadata(currentDevice.id, metadata);

      await refreshDevices();

      // toast.success(`Device "${metadata.name}" updated successfully`);

      onClose && onClose();
    } catch (error) {
      console.error('Error saving device:', error);
      toast.error('Failed to update device');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (currentDevice) {
      const deviceValues = {
        name: currentDevice.name || '',
        location: currentDevice.location || '',
        floor: currentDevice.floor || '',
        type: currentDevice.type || '',
      };
      setOriginal(deviceValues);
      setDraft(deviceValues);
    }
  }, [deviceId, currentDevice?.name, currentDevice?.location, currentDevice?.floor, currentDevice?.type]);

  const allLocations = getAllLocations(devices);
  const allFloors = getAllFloors(devices);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="device-name" className="text-xs font-medium text-foreground">
          Name
        </Label>
        <Input
          id="device-name"
          value={draft.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="Enter device name"
          className={cn(
            "h-11 text-sm",
            !isValidName(draft.name) && hasChanges
              ? "border-destructive"
              : "border-border"
          )}
        />
        {!isValidName(draft.name) && hasChanges && (
          <p className="text-xs text-destructive">Name is required</p>
        )}
      </div>

      <div className="space-y-1">
        <Label className="text-xs font-medium text-foreground">
          Location
        </Label>
        <PickerSheet
          title="Location"
          value={draft.location}
          onChange={(value) => handleChange('location', value)}
          options={allLocations}
          placeholder="Select location"
          emptyLabel="Unassigned"
          allowAddNew={true}
          validateAddNew={(value) => {
            if (isDuplicateLocation(value)) {
              toast.error('Location already exists');
              return false;
            }
            return true;
          }}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs font-medium text-foreground">
          Floor
        </Label>
        <PickerSheet
          title="Floor"
          value={draft.floor}
          onChange={(value) => handleChange('floor', value)}
          options={allFloors}
          placeholder="Select floor"
          emptyLabel="Not set"
          allowAddNew={true}
          validateAddNew={(value) => {
            if (isDuplicateFloor(value)) {
              toast.error('Floor already exists');
              return false;
            }
            return true;
          }}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs font-medium text-foreground">
          Type
        </Label>
        <PickerSheet
          title="Type"
          value={draft.type}
          onChange={(value) => handleChange('type', value)}
          options={PREDEFINED_DEVICE_TYPES}
          placeholder="Select type"
          emptyLabel="Not set"
          allowAddNew={false}
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          type="button"
          variant="secondary"
          onClick={handleCancel}
          disabled={isSaving}
          className="flex-1 h-11 text-sm"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || !isValid || isSaving}
          className="flex-1 h-11 text-sm"
        >
          {isSaving ? 'Saving...' : 'Done'}
        </Button>
      </div>
    </div>
  );
};

export default DeviceEditor;
